import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "./api";
import { useAuth } from "./AuthContext";
import Loader from "./Loader";
import heroVideo from "../assets/dgb-hero.mp4";
import { PALETTE, GLOW, MOTION } from "../design-tools/tokens";

/* ─────────────────────────────────────────────
   Blueprint corner glyph (inline — no import needed)
───────────────────────────────────────────── */
function Corner({ position = "tl", size = 28 }) {
  const flipX = position.includes("r");
  const flipY = position.includes("b");
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})` }}
    >
      <line x1="0" y1="0" x2={size * 0.65} y2="0" stroke={PALETTE.teal700} strokeWidth="0.8" opacity="0.7" />
      <line x1="0" y1="0" x2="0" y2={size * 0.65} stroke={PALETTE.teal700} strokeWidth="0.8" opacity="0.7" />
      <circle cx={size * 0.2} cy={size * 0.2} r="2" fill="none" stroke={PALETTE.teal700} strokeWidth="0.5" opacity="0.6" />
    </svg>
  );
}

export default function Login() {
  const { refresh } = useAuth();
  const navigate    = useNavigate();
  const location    = useLocation();
  const from        = location.state?.from?.pathname || "/dashboard";

  const [showLoader, setShowLoader] = useState(true);
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [err,      setErr]      = useState("");
  const [busy,     setBusy]     = useState(false);

  /* Focus the email field once the loader clears */
  const emailRef = useRef();
  useEffect(() => {
    if (!showLoader) emailRef.current?.focus();
  }, [showLoader]);

  if (showLoader) return <Loader onDone={() => setShowLoader(false)} label="WELCOME BACK" />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/login", { email, password });
      await refresh();
      navigate(from, { replace: true });
    } catch (ex) {
      setErr(ex?.response?.data?.error || "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.root}>
      {/* ── Video background ── */}
      <video autoPlay muted loop playsInline style={S.video}>
        <source src={heroVideo} type="video/mp4" />
      </video>

      {/* ── Gradient overlay ── */}
      <div aria-hidden="true" style={S.overlay} />

      {/* ── Blueprint corners ── */}
      <div style={{ position: "fixed", top: 28, left: 28, zIndex: 3 }}><Corner position="tl" /></div>
      <div style={{ position: "fixed", top: 28, right: 28, zIndex: 3 }}><Corner position="tr" /></div>
      <div style={{ position: "fixed", bottom: 28, left: 28, zIndex: 3 }}><Corner position="bl" /></div>
      <div style={{ position: "fixed", bottom: 28, right: 28, zIndex: 3 }}><Corner position="br" /></div>

      {/* ── Scan line ── */}
      <div aria-hidden="true" style={S.scanLine} />

      {/* ── Telemetry — top left ── */}
      <div style={S.telemTL}>
        <div>N 40°43′ &nbsp; W 74°00′</div>
        <div style={{ opacity: 0.5 }}>◌ &nbsp; ATELIER 01 &nbsp; ◌ &nbsp; SECURE</div>
      </div>

      {/* ── Card ── */}
      <div style={S.card}>
        {/* Card inner glow line */}
        <div aria-hidden="true" style={S.cardTopLine} />

        <p style={S.eyebrow}>◈ &nbsp; DIGITAL BILLIONS GROUP</p>

        <h1 style={S.h1}>
          Welcome{" "}
          <em style={{ fontStyle: "italic", color: PALETTE.teal400, textShadow: GLOW.text }}>
            back.
          </em>
        </h1>

        <p style={S.lede}>Sign in to your account to continue.</p>

        <div style={S.divider} />

        <form onSubmit={submit} noValidate>
          <label style={S.label}>Email Address</label>
          <input
            ref={emailRef}
            style={S.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            onFocus={(e) => (e.target.style.borderColor = PALETTE.teal600)}
            onBlur={(e)  => (e.target.style.borderColor = "rgba(53,143,128,0.25)")}
          />

          <label style={{ ...S.label, marginTop: 20 }}>Password</label>
          <input
            style={S.input}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            onFocus={(e) => (e.target.style.borderColor = PALETTE.teal600)}
            onBlur={(e)  => (e.target.style.borderColor = "rgba(53,143,128,0.25)")}
          />

          {err && (
            <div style={S.err}>
              <span style={{ color: PALETTE.teal500 }}>◌</span> {err}
            </div>
          )}

          <button type="submit" disabled={busy} style={S.btn}
            onMouseEnter={(e) => { if (!busy) { e.currentTarget.style.background = PALETTE.bgElevated; e.currentTarget.style.borderColor = PALETTE.teal600; e.currentTarget.style.letterSpacing = "0.22em"; }}}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(54,196,168,0.06)"; e.currentTarget.style.borderColor = "rgba(53,143,128,0.3)"; e.currentTarget.style.letterSpacing = "0.18em"; }}>
            <span>{busy ? "Signing in…" : "Continue"}</span>
            <span aria-hidden="true">→</span>
          </button>
        </form>

        <div style={S.altRow}>
          <Link to="/" style={S.altLink}
            onMouseEnter={(e) => (e.target.style.color = PALETTE.teal400)}
            onMouseLeave={(e) => (e.target.style.color = PALETTE.teal700)}>
            ← Back to site
          </Link>
          <Link to="/signup" style={S.altLink}
            onMouseEnter={(e) => (e.target.style.color = PALETTE.teal400)}
            onMouseLeave={(e) => (e.target.style.color = PALETTE.teal700)}>
            Create account →
          </Link>
        </div>

        {/* Bottom mono tag */}
        <p style={S.cardFooter}>DBG &nbsp; ◌ &nbsp; SECURE LOGIN &nbsp; ◌ &nbsp; MMXXVI</p>
      </div>

      {/* ── Bottom telemetry ── */}
      <div style={S.telemBottom}>
        SYS:READY &nbsp; ◌ &nbsp; AUTH NODE &nbsp; ◌ &nbsp; GLOBAL
      </div>
    </div>
  );
}

/* ─── Styles ──────────────────────────────── */
const S = {
  root: {
    minHeight: "100svh",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 24px",
    background: PALETTE.ink,
    overflow: "hidden",
  },

  video: {
    position: "fixed",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    opacity: 0.18,
    zIndex: 0,
    pointerEvents: "none",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1,
    pointerEvents: "none",
    background: `radial-gradient(ellipse at 65% 35%, rgba(54,196,168,0.07), transparent 55%),
                 linear-gradient(180deg, rgba(10,18,16,0.65) 0%, rgba(10,18,16,0.88) 100%)`,
  },

  scanLine: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, rgba(54,196,168,0.25), transparent)`,
    animation: "scanLine 6s linear infinite",
    zIndex: 3,
    pointerEvents: "none",
  },

  telemTL: {
    position: "fixed",
    top: 36,
    left: 64,
    zIndex: 4,
    fontFamily: "var(--font-mono)",
    fontSize: "9px",
    letterSpacing: "0.28em",
    textTransform: "uppercase",
    color: PALETTE.teal700,
    lineHeight: 1.9,
  },

  telemBottom: {
    position: "fixed",
    bottom: 28,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 4,
    fontFamily: "var(--font-mono)",
    fontSize: "9px",
    letterSpacing: "0.28em",
    textTransform: "uppercase",
    color: "rgba(36,130,119,0.4)",
    whiteSpace: "nowrap",
  },

  card: {
    position: "relative",
    zIndex: 5,
    width: "min(460px, 100%)",
    background: "linear-gradient(160deg, #0f2018 0%, #0a1a16 100%)",
    border: "1px solid rgba(53,143,128,0.22)",
    padding: "44px 40px 36px",
    boxShadow: "0 0 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(54,196,168,0.04)",
  },

  cardTopLine: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: "linear-gradient(90deg, transparent, rgba(54,196,168,0.5), transparent)",
  },

  eyebrow: {
    fontFamily: "var(--font-mono)",
    fontSize: "9px",
    letterSpacing: "0.32em",
    textTransform: "uppercase",
    color: PALETTE.teal700,
    marginBottom: 28,
  },

  h1: {
    fontFamily: "var(--font-display)",
    fontWeight: 300,
    fontSize: "clamp(42px, 6vw, 58px)",
    lineHeight: 0.95,
    letterSpacing: "-0.02em",
    color: PALETTE.white,
    marginBottom: 14,
  },

  lede: {
    fontFamily: "var(--font-body)",
    fontWeight: 300,
    fontSize: "14px",
    lineHeight: 1.75,
    color: PALETTE.whiteSoft,
    marginBottom: 28,
  },

  divider: {
    height: "1px",
    background: "linear-gradient(90deg, rgba(53,143,128,0.25), transparent)",
    marginBottom: 28,
  },

  label: {
    display: "block",
    fontFamily: "var(--font-mono)",
    fontSize: "9px",
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    color: PALETTE.teal700,
    marginBottom: 7,
  },

  input: {
    width: "100%",
    background: "rgba(10,18,16,0.6)",
    border: "1px solid rgba(53,143,128,0.25)",
    padding: "12px 14px",
    fontFamily: "var(--font-body)",
    fontSize: "14px",
    color: PALETTE.white,
    outline: "none",
    transition: `border-color ${MOTION.fast} ease`,
    caretColor: PALETTE.teal400,
  },

  err: {
    fontFamily: "var(--font-mono)",
    fontSize: "10px",
    letterSpacing: "0.18em",
    color: "#e07060",
    marginTop: 18,
    lineHeight: 1.5,
  },

  btn: {
    width: "100%",
    marginTop: 28,
    padding: "14px 20px",
    fontFamily: "var(--font-mono)",
    fontSize: "11px",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    background: "rgba(54,196,168,0.06)",
    color: PALETTE.white,
    border: "1px solid rgba(53,143,128,0.3)",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: `all ${MOTION.fast} ${MOTION.ease}`,
  },

  altRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 22,
    fontFamily: "var(--font-mono)",
    fontSize: "10px",
    letterSpacing: "0.18em",
  },

  altLink: {
    color: PALETTE.teal700,
    textDecoration: "none",
    transition: `color ${MOTION.fast} ease`,
  },

  cardFooter: {
    fontFamily: "var(--font-mono)",
    fontSize: "8px",
    letterSpacing: "0.28em",
    textTransform: "uppercase",
    color: "rgba(36,130,119,0.35)",
    marginTop: 32,
    paddingTop: 20,
    borderTop: "1px solid rgba(53,143,128,0.1)",
  },
};
